import type { CareerPlanGoal } from "./careerPlanHistory";
import {
  CAREER_INTENT_OPTIONS,
  CLARITY_OPTIONS,
  FACILITY_TYPE_OPTIONS,
  formatRankedLabels,
  getOptionLabel,
  getRoleCode,
  getRoleLabel,
  getStateLabel,
  IMPROVEMENT_AREA_OPTIONS,
  MOTIVATION_OPTIONS,
  OPEN_TO_ANYWHERE,
  RETIREMENT_TYPE_OPTIONS,
  TIMEFRAME_OPTIONS,
  type CareerIntentId,
  type CareerIntentSummaryGroupConfig
} from "./careerIntentCatalog";

export type RetirementType = "full" | "partial" | "uncertain";
export type Timeframe = "under_3_months" | "3_6_months" | "6_12_months" | "1_2_years" | "3_5_years" | "5_plus_years";

export type CareerIntentFormValues = {
  selectedIntents: CareerIntentId[];
  targetPositions: string[];
  locations: string[];
  facilityTypes: string[];
  motivations: string[];
  improvementAreas: string[];
  additionalContext: string;
  timeframe: Timeframe | null;
  clarity: number | null;
  retirementType: RetirementType | null;
  hasNoDesiredChanges: boolean;
};

export type UpsertCareerIntentInput = {
  pgaId: string;
  targetPositions: string[];
  locations: string[];
  facilityTypes: string[];
  motivations: string[];
  improvementAreas: string[];
  additionalContext: string | null;
  timeframe: Timeframe | null;
  clarity: number | null;
  retirementType: RetirementType | null;
  hasNoDesiredChanges: boolean;
};

export type CareerIntentStepId =
  | "intents"
  | "improvement_areas"
  | "target_positions"
  | "locations"
  | "facility_types"
  | "retirement_type"
  | "motivations"
  | "timing"
  | "notes";

export type CareerIntentStep = {
  id: CareerIntentStepId;
  title: string;
};

export const RANKED_SELECTION_MAX = 3;
export const RANKED_SELECTION_HINT = "Select up to 3 in order of importance. 1 is most important.";
export const RANKED_LOCATION_HINT =
  "Select up to 3 in order of importance. 1 is most important. Leave blank if you have no preference.";
export const RANKED_POSITION_HINT = "Select up to 3 positions in order of importance. 1 is most important.";
export const RANKED_POSITION_SUGGESTED_HINT = `${RANKED_POSITION_HINT} Suggested for you:`;

export function toggleRankedSelection(
  selected: string[],
  value: string,
  maxSelections = RANKED_SELECTION_MAX
): string[] {
  if (selected.includes(value)) {
    return selected.filter((item) => item !== value);
  }

  return selected.length >= maxSelections ? selected : [...selected, value];
}

export function moveRankedSelection(selected: string[], index: number, direction: -1 | 1): string[] {
  const nextIndex = index + direction;
  if (index < 0 || nextIndex < 0 || index >= selected.length || nextIndex >= selected.length) {
    return selected;
  }

  const next = [...selected];
  const [item] = next.splice(index, 1);
  next.splice(nextIndex, 0, item);
  return next;
}

export function rankedSelectionIndex(selected: string[], value: string): number | undefined {
  const index = selected.indexOf(value);
  return index === -1 ? undefined : index + 1;
}

export const CAREER_INTENT_STEP_TITLES: Record<CareerIntentStepId, string> = {
  intents: "Right now, I’m interested in…",
  improvement_areas: "What do you want to improve about your current position?",
  target_positions: "Which position(s) are you targeting next?",
  locations: "Which states are you open to?",
  facility_types: "What facility types interest you?",
  retirement_type: "What kind of retirement are you thinking about?",
  motivations: "What’s informing this next step for you?",
  timing: "For what you’re exploring next…",
  notes: "Anything else we should know?"
};

export function defaultCareerIntentFormValues(): CareerIntentFormValues {
  return {
    selectedIntents: [],
    targetPositions: [],
    locations: [],
    facilityTypes: [],
    motivations: [],
    improvementAreas: [],
    additionalContext: "",
    timeframe: null,
    clarity: null,
    retirementType: null,
    hasNoDesiredChanges: false
  };
}

const ACTIVE_INTENTS: CareerIntentId[] = ["improve", "new_role", "location", "facility", "retirement"];

export function activeCareerIntentIds(values: Pick<CareerIntentFormValues, "selectedIntents">): CareerIntentId[] {
  return values.selectedIntents.filter((intent) => ACTIVE_INTENTS.includes(intent));
}

export function isRetirementOnly(values: Pick<CareerIntentFormValues, "selectedIntents">): boolean {
  const active = activeCareerIntentIds(values);
  return active.length === 1 && active[0] === "retirement";
}

export function toggleCareerIntent(values: CareerIntentFormValues, intent: CareerIntentId): CareerIntentFormValues {
  if (intent === "none_for_now") {
    return values.selectedIntents.includes(intent)
      ? { ...values, selectedIntents: [], hasNoDesiredChanges: false }
      : {
          ...values,
          selectedIntents: ["none_for_now"],
          hasNoDesiredChanges: true
        };
  }

  const selectedIntents = values.selectedIntents.filter((selected) => selected !== "none_for_now");
  const nextSelectedIntents = selectedIntents.includes(intent)
    ? selectedIntents.filter((selected) => selected !== intent)
    : [...selectedIntents, intent];

  return {
    ...values,
    selectedIntents: nextSelectedIntents,
    hasNoDesiredChanges: false
  };
}

export function getCareerIntentSteps(values: Pick<CareerIntentFormValues, "selectedIntents">): CareerIntentStep[] {
  const active = activeCareerIntentIds(values);
  const steps: CareerIntentStep[] = [{ id: "intents", title: CAREER_INTENT_STEP_TITLES.intents }];

  if (active.includes("improve")) {
    steps.push({ id: "improvement_areas", title: CAREER_INTENT_STEP_TITLES.improvement_areas });
  }
  if (active.includes("new_role")) {
    steps.push({ id: "target_positions", title: CAREER_INTENT_STEP_TITLES.target_positions });
  }
  if (active.includes("location")) {
    steps.push({ id: "locations", title: CAREER_INTENT_STEP_TITLES.locations });
  }
  if (active.includes("facility")) {
    steps.push({ id: "facility_types", title: CAREER_INTENT_STEP_TITLES.facility_types });
  }
  if (active.includes("retirement")) {
    steps.push({ id: "retirement_type", title: CAREER_INTENT_STEP_TITLES.retirement_type });
  }
  if (active.length > 0) {
    if (!isRetirementOnly(values)) {
      steps.push({ id: "motivations", title: CAREER_INTENT_STEP_TITLES.motivations });
    }
    steps.push({ id: "timing", title: CAREER_INTENT_STEP_TITLES.timing });
  }

  steps.push({
    id: "notes",
    title: values.selectedIntents.includes("none_for_now")
      ? "Anything you’d like your consultant to know?"
      : CAREER_INTENT_STEP_TITLES.notes
  });

  return steps;
}

export function hydrateCareerIntentForm(record?: CareerPlanGoal | null): CareerIntentFormValues {
  if (!record) {
    return defaultCareerIntentFormValues();
  }

  if (record.hasNoDesiredChanges) {
    return {
      ...defaultCareerIntentFormValues(),
      selectedIntents: ["none_for_now"],
      hasNoDesiredChanges: true,
      additionalContext: record.additionalContext ?? ""
    };
  }

  const selectedIntents: CareerIntentId[] = [];
  if (record.improvementAreas.length > 0) selectedIntents.push("improve");
  if (record.targetPositions.length > 0) selectedIntents.push("new_role");
  if (record.locations.length > 0) selectedIntents.push("location");
  if (record.facilityTypes.length > 0) selectedIntents.push("facility");
  if (record.retirementType) selectedIntents.push("retirement");

  return {
    selectedIntents,
    targetPositions: record.targetPositions.map((value) => getRoleCode(value) ?? value.trim()),
    locations: record.locations.filter((location) => location !== OPEN_TO_ANYWHERE),
    facilityTypes: [...record.facilityTypes],
    motivations: [...record.motivations],
    improvementAreas: [...record.improvementAreas],
    additionalContext: record.additionalContext ?? "",
    timeframe: isTimeframe(record.timeframe) ? record.timeframe : null,
    clarity: isClarity(record.clarity) ? record.clarity : null,
    retirementType: isRetirementType(record.retirementType) ? record.retirementType : null,
    hasNoDesiredChanges: false
  };
}

export function buildUpsertCareerIntentInput(pgaId: string, values: CareerIntentFormValues): UpsertCareerIntentInput {
  const noDesiredChanges = values.selectedIntents.includes("none_for_now") || values.hasNoDesiredChanges;
  const active = new Set(activeCareerIntentIds(values));
  const hasRelocation = active.has("location");
  const targetPositions = Array.from(
    new Set(values.targetPositions.map((value) => getRoleLabel(value) ?? value.trim()).filter(Boolean))
  );

  return {
    pgaId,
    targetPositions: noDesiredChanges || !active.has("new_role") ? [] : targetPositions,
    locations:
      noDesiredChanges || !hasRelocation
        ? []
        : values.locations.length > 0
          ? [...values.locations]
          : [OPEN_TO_ANYWHERE],
    facilityTypes: noDesiredChanges || !active.has("facility") ? [] : [...values.facilityTypes],
    motivations:
      noDesiredChanges ||
      (!active.has("improve") && !active.has("new_role") && !active.has("location") && !active.has("facility"))
        ? []
        : [...values.motivations],
    improvementAreas: noDesiredChanges || !active.has("improve") ? [] : [...values.improvementAreas],
    additionalContext: values.additionalContext.trim() || null,
    timeframe: noDesiredChanges ? null : values.timeframe,
    clarity: noDesiredChanges ? null : values.clarity,
    retirementType: noDesiredChanges || !active.has("retirement") ? null : values.retirementType,
    hasNoDesiredChanges: noDesiredChanges
  };
}

export function validateCareerIntentStep(values: CareerIntentFormValues, step: CareerIntentStepId): string | undefined {
  switch (step) {
    case "intents":
      return values.selectedIntents.length > 0 ? undefined : "Select at least one option to continue.";
    case "improvement_areas":
      return values.improvementAreas.length > 0 ? undefined : "Select at least one area to improve.";
    case "target_positions":
      return values.targetPositions.length > 0 ? undefined : "Select at least one target position.";
    case "locations":
      return undefined;
    case "facility_types":
      return values.facilityTypes.length > 0 ? undefined : "Select at least one facility type.";
    case "retirement_type":
      return values.retirementType ? undefined : "Select a retirement option to continue.";
    case "motivations":
      return values.motivations.length > 0 ? undefined : "Select at least one thing informing this next step.";
    case "timing":
      return values.timeframe && values.clarity ? undefined : "Select a timeframe and clarity value to continue.";
    case "notes":
      return undefined;
    default: {
      const _exhaustive: never = step;
      return _exhaustive;
    }
  }
}

export type CareerIntentSummaryRow = {
  label: string;
  values: string[];
};

export const FORM_SUMMARY_GROUPS: CareerIntentSummaryGroupConfig[] = [
  { id: "direction", labels: ["You’re exploring"] },
  { id: "improvement", labels: ["What you want to improve"] },
  { id: "targetPositions", labels: ["Target positions"] },
  { id: "location", labels: ["States"] },
  { id: "facility", labels: ["Facility types"] },
  { id: "retirement", labels: ["Retirement"] },
  {
    id: "timing",
    heading: "Timing and Motivation",
    labels: ["What’s informing this", "Timeframe", "Clarity on next steps"]
  },
  { id: "notes", labels: ["Notes for your consultant"] }
];

export function getCareerIntentSummaryRows(
  values: CareerIntentFormValues,
  includeNotes = false
): CareerIntentSummaryRow[] {
  const rows: CareerIntentSummaryRow[] = [];
  const active = activeCareerIntentIds(values);

  if (values.selectedIntents.length > 0) {
    rows.push({
      label: "You’re exploring",
      values: values.selectedIntents
        .map((intent) => getOptionLabel(CAREER_INTENT_OPTIONS, intent) ?? intent)
        .map((label) => {
          return label === "Thinking about retirement" ? "Retirement" : label;
        })
    });
  }
  if (active.includes("improve") && values.improvementAreas.length > 0) {
    rows.push({
      label: "What you want to improve",
      values: formatRankedLabels(
        values.improvementAreas.map((value) => getOptionLabel(IMPROVEMENT_AREA_OPTIONS, value) ?? value)
      )
    });
  }
  if (active.includes("new_role") && values.targetPositions.length > 0) {
    rows.push({
      label: "Target positions",
      values: formatRankedLabels(values.targetPositions.map((value) => getRoleLabel(value) ?? value))
    });
  }
  if (active.includes("location") && values.locations.length > 0) {
    rows.push({
      label: "States",
      values: formatRankedLabels(values.locations.map((value) => getStateLabel(value) ?? value))
    });
  }
  if (active.includes("facility") && values.facilityTypes.length > 0) {
    rows.push({
      label: "Facility types",
      values: formatRankedLabels(
        values.facilityTypes.map((value) => getOptionLabel(FACILITY_TYPE_OPTIONS, value) ?? value)
      )
    });
  }
  if (active.includes("retirement") && values.retirementType) {
    rows.push({
      label: "Retirement",
      values: [getOptionLabel(RETIREMENT_TYPE_OPTIONS, values.retirementType) ?? values.retirementType]
    });
  }
  if (active.length > 0 && values.motivations.length > 0) {
    rows.push({
      label: "What’s informing this",
      values: formatRankedLabels(values.motivations.map((value) => getOptionLabel(MOTIVATION_OPTIONS, value) ?? value))
    });
  }
  if (active.length > 0 && values.timeframe) {
    rows.push({
      label: "Timeframe",
      values: [getOptionLabel(TIMEFRAME_OPTIONS, values.timeframe) ?? values.timeframe]
    });
  }
  if (active.length > 0 && values.clarity) {
    rows.push({
      label: "Clarity on next steps",
      values: [getOptionLabel(CLARITY_OPTIONS, String(values.clarity)) ?? String(values.clarity)]
    });
  }
  if (includeNotes && values.additionalContext.trim()) {
    rows.push({ label: "Notes for your consultant", values: [values.additionalContext.trim()] });
  }

  return rows;
}

function isTimeframe(value?: string | null): value is Timeframe {
  return TIMEFRAME_OPTIONS.some((option) => option.value === value);
}

function isClarity(value?: number | null): value is number {
  return value != null && CLARITY_OPTIONS.some((option) => option.value === String(value));
}

function isRetirementType(value?: string | null): value is RetirementType {
  return RETIREMENT_TYPE_OPTIONS.some((option) => option.value === value);
}
